import * as CONSTS from '../constants.js';
import { graphemesOf, graphemeCount, graphemeToCodeUnit, codeUnitToGrapheme } from '../text_segment.js';

export default class Content {
  /**
   * コンストラクタ
   * @param {Object} content redo, undo用の操作履歴
   */
  constructor(content = null) {
    this._key = this.paramFromObject(content, 'key');
    this._uuid = this.paramFromObject(content, 'uuid', crypto.randomUUID());
    this._body = this.paramFromObject(content, 'body', '');
    this._updatingbody = this.paramFromObject(content, 'body', '');
    this._tag_type = this.paramFromObject(content, 'tag_type');
    this._sort_key = this.paramFromObject(content, 'sort_key');
    this._separate = this.paramFromObject(content, 'separate');
    this._hashira_index = this.paramFromObject(content, 'hashira_index');
    this._status = this.paramFromObject(content, 'status');
    this._next = this.paramFromObject(content, 'next');
    this._previous = this.paramFromObject(content, 'previous');
    this._selectFrom = -1;
    this._selectTo = -1;
    this._style = this.styleFromObject(content);
  }

  paramFromObject(content, param, initValue = null) {
    return content == null || content[param] == null ? initValue : content[param];
  }

  styleFromObject(content) {
    if (content == null || content['style'] == null) return [];
    return content['style'].map((style) => {
      return {
        style_type: style['style_type'],
        // 現状色の指定ができないため、style_typeのみでスタイルを管理する
        // color: style['color'],
        start_index: style['start_index'],
        end_index: style['end_index'],
      };
    });
  }

  formatParams() {
    let formData = new FormData();
    const appendFormData = function (name, value) {
      if (value == null) value = '';
      formData.append(`content[${name}]`, value);
    };
    appendFormData('key', this.key);
    appendFormData('body', this.body);
    appendFormData('tag_type', this.tag_type);
    appendFormData('sort_key', this.sort_key);
    appendFormData('separate', this.separate);
    appendFormData('hashira_index', this.hashira_index);
    appendFormData('status', this.status);

    return formData;
  }

  /**
   * ObjectからOperationRecordクラスへ変換する
   * @param {Object} obj オブジェクト
   * @returns {OperationRecord} OperationRecord
   */
  static fromObject(obj = null) {
    return new OperationRecord(obj);
  }

  /**
   * html要素が混じっているテキストを純粋なテキストに変換する
   */
  fixBodyText() {
    if (this.body.includes('</')) {
      let tempEl = document.createElement('div');
      tempEl.innerHTML = this.body;
      while (tempEl.firstElementChild) {
        tempEl = tempEl.firstElementChild;
      }
      this.body = tempEl.textContent;
    }
    if (this.body.includes('\n')) {
      this.body = this.body.replace('\n', '');
    }
  }

  // タグタイプを変更し、セリフに変更するときはキャラ名の長さを正規化する
  // キャラ名の変更により文字数が増えた分のオフセットを返す
  changeTagType(newTagType) {
    this.tag_type = newTagType;
    let offset = 0;
    // キャラ名を3文字以上に正規化
    if (newTagType == 'serifu') {
      let charaName = this.body.split('「')[0];
      switch (charaName.length) {
        case 1:
          this.body = this.body.replace(charaName, '　' + charaName + '　');
          offset = 2;
          break;
        case 2:
          this.body = this.body.replace(charaName, charaName.split('')[0] + '　' + charaName.split('')[1]);
          offset = 1;
          break;
      }
    } else if (newTagType == 'togaki') {
      // 「が消える前の位置が取得されてしまうので、offsetで調整
      offset = -1;
    }
    this.status = 'editing';
    return offset;
  }

  countProtrusion(index = this.graphemeLength - 1) {
    if (this.graphemeLength == 0) return 0;
    this.fixBodyText();
    // 書記素配列で扱う（環境依存文字を1論理文字として数える）
    let line = graphemesOf(this._updatingbody).slice(0, index);
    let num = 1;
    let countProtrusion = 0;
    let beforeLine = 0;
    const puncts = ['。', '、', '」', '』', '）'];
    if (this.tag_type == 'serifu') {
      while (line.length > beforeLine) {
        beforeLine = CONSTS.FIRST_SERIFU_MAX_COUNT + CONSTS.SERIFU_MAX_COUNT * (num - 1);
        if (puncts.includes(line[beforeLine])) {
          line.splice(beforeLine, 1);
          countProtrusion++;
        }
        num++;
      }
    } else if (this.tag_type == 'hashira') {
      while (line.length > beforeLine) {
        beforeLine = CONSTS.HASHIRA_MAX_COUNT * num;
        if (puncts.includes(line[beforeLine])) {
          line.splice(beforeLine, 1);
          countProtrusion++;
        }
        num++;
      }
    } else {
      // ト書
      while (line.length > beforeLine) {
        beforeLine = CONSTS.TOGAKI_MAX_COUNT * num;
        if (puncts.includes(line[beforeLine])) {
          line.splice(beforeLine, 1);
          countProtrusion++;
        }
        num++;
      }
    }
    return countProtrusion;
  }

  updateBody(updateText, updateTagType = true) {
    this._body = updateText;
    let serifuOffset = 0;
    if (updateTagType) {
      if (this.body.includes('「')) {
        if (this.tag_type == 'togaki') {
          this._tag_type = 'serifu';
          let serifuStringArray = this.body.split('「');
          if (serifuStringArray[0].length == 1) {
            this._body = '　' + serifuStringArray[0] + '　「' + this.body.slice(2);
            serifuOffset = 2;
          } else if (serifuStringArray[0].length == 2) {
            this.body = serifuStringArray[0][0] + '　' + serifuStringArray[0][1] + '「' + this.body.slice(3);
            serifuOffset = 1;
          }
        }
      } else {
        if (this.tag_type == 'serifu') {
          this._tag_type = 'togaki';
        }
      }
    }
    this._updatingbody = this.body;
    return serifuOffset;
  }

  get lastWordIndex() {
    switch (this._tag_type) {
      case 'togaki': {
        const mod = (this.graphemeLength - this.countProtrusion(this.graphemeLength)) % CONSTS.TOGAKI_MAX_COUNT;
        return (mod === 0 && this.graphemeLength > 0 ? CONSTS.TOGAKI_MAX_COUNT : mod) + 6;
      }
      case 'hashira': {
        const mod = (this.graphemeLength - this.countProtrusion(this.graphemeLength)) % CONSTS.HASHIRA_MAX_COUNT;
        return mod === 0 && this.graphemeLength > 0 ? CONSTS.HASHIRA_MAX_COUNT : mod;
      }
      case 'serifu':
        if (
          this.graphemeLength - this.countProtrusion(CONSTS.FIRST_SERIFU_MAX_COUNT + 1) <=
          CONSTS.FIRST_SERIFU_MAX_COUNT
        ) {
          return this.graphemeLength;
        } else {
          const mod =
            (this.graphemeLength - CONSTS.FIRST_SERIFU_MAX_COUNT - this.countProtrusion(this.graphemeLength)) %
            CONSTS.SERIFU_MAX_COUNT;
          return (mod === 0 ? CONSTS.SERIFU_MAX_COUNT : mod) + 4;
        }
      default:
        return 0;
    }
  }

  get lineNum() {
    if (this.graphemeLength == 0) return 1;
    this.fixBodyText();
    // 書記素配列で扱う
    let line = graphemesOf(this._updatingbody);
    let num = 0;
    let beforeLine = 0;
    const puncts = ['。', '、', '」', '』', '）'];
    if (this.tag_type == 'serifu') {
      while (line.length > beforeLine) {
        num++;
        beforeLine = CONSTS.FIRST_SERIFU_MAX_COUNT + CONSTS.SERIFU_MAX_COUNT * (num - 1);
        if (puncts.includes(line[beforeLine])) {
          line.splice(beforeLine, 1);
        }
      }
    } else if (this.tag_type == 'hashira') {
      while (line.length > beforeLine) {
        num++;
        beforeLine = CONSTS.HASHIRA_MAX_COUNT * num;
        if (puncts.includes(line[beforeLine])) {
          line.splice(beforeLine, 1);
        }
      }
    } else {
      // ト書
      while (line.length > beforeLine) {
        num++;
        beforeLine = CONSTS.TOGAKI_MAX_COUNT * num;
        if (puncts.includes(line[beforeLine])) {
          line.splice(beforeLine, 1);
        }
      }
    }
    return num;
  }

  /**
   * key
   * @returns {number}
   */
  get key() {
    return this._key;
  }

  set key(value) {
    this._key = value;
  }

  /**
   * uuid
   * @returns {number}
   */
  get uuid() {
    return this._uuid;
  }

  set uuid(value) {
    this._uuid = value;
  }

  /**
   * body
   * @returns {number}
   */
  get body() {
    return this._body;
  }

  set body(value) {
    this._body = value;
  }

  /**
   * 行数カウント用のcompositionupdateで更新するbody
   * @returns {number}
   */
  get updatingbody() {
    return this._updatingbody;
  }

  set updatingbody(value) {
    this._updatingbody = value;
  }

  /**
   * tag_type
   * @returns {number}
   */
  get tag_type() {
    return this._tag_type;
  }

  set tag_type(value) {
    this._tag_type = value;
  }

  /**
   * sort_key
   * @returns {number}
   */
  get sort_key() {
    return this._sort_key;
  }

  set sort_key(value) {
    this._sort_key = value;
  }

  /**
   * separate
   * @returns {number}
   */
  get separate() {
    return this._separate;
  }

  set separate(value) {
    this._separate = value;
  }

  /**
   * hashira_index
   * @returns {array}
   */
  get hashira_index() {
    return this._hashira_index;
  }

  set hashira_index(value) {
    this._hashira_index = value;
  }

  /**
   * status
   * @returns {string}
   */
  get status() {
    return this._status;
  }

  set status(value) {
    this._status = value;
  }

  /**
   * next
   * @returns {Content}
   */
  get next() {
    return this._next;
  }

  set next(value) {
    this._next = value;
  }

  /**
   * previous
   * @returns {Content}
   */
  get previous() {
    return this._previous;
  }

  set previous(value) {
    this._previous = value;
  }

  /**
   * style
   * @returns {array}
   */
  get style() {
    return this._style;
  }

  set style(value) {
    this._style = value;
  }

  /**
   * updatingbody の書記素数（＝論理的な文字数）。
   * 文字列の .length（コードユニット数）の代替として使う。
   * @returns {number}
   */
  get graphemeLength() {
    return graphemeCount(this._updatingbody);
  }

  /**
   * body の書記素数（＝論理的な文字数）。
   * @returns {number}
   */
  get bodyGraphemeLength() {
    return graphemeCount(this._body);
  }

  /**
   * style を「書記素基準 → コードユニット基準」に変換したコピーを返す。
   * DB / .stry など永続層へ保存する境界で使う（in-memory は書記素基準）。
   * @returns {{style_type: string, start_index: number, end_index: number}[]}
   */
  styleAsCodeUnit() {
    return this._style.map((style) => ({
      style_type: style.style_type,
      start_index: graphemeToCodeUnit(this._body, style.start_index),
      end_index: graphemeToCodeUnit(this._body, style.end_index),
    }));
  }

  /**
   * 保持している style を「コードユニット基準 → 書記素基準」へ変換する（破壊的）。
   * サーバから読み込んだ直後の境界でのみ呼ぶ。
   */
  convertStyleFromCodeUnit() {
    this._style = this._style.map((style) => ({
      style_type: style.style_type,
      start_index: codeUnitToGrapheme(this._body, style.start_index),
      end_index: codeUnitToGrapheme(this._body, style.end_index),
    }));
  }
}
